/**
 * The project budget's words and Hugin's unasked suggestions on it (issue #132, in the «Hugin ser N
 * ting» pattern of #131). Fixed rules over the server's figures (projectBudgetController.js), no
 * model: a job whose forecast is over its budget, a job past the warning with no progress said, and
 * the month about to close with projects whose hours are not approved. Tested in budget.test.ts.
 */
import type { ProjectBudget, ProjectGroup } from '@/api/projects';
import { T, useI18n } from '@/i18n';
import type { Nudge } from '../vaktplan/advice';

/** 14 and 12,5: hours to one decimal, as Norwegian writes them. */
export const h1 = (n: number) => (Math.round(n * 10) / 10).toLocaleString('nb-NO');
/** kr 59 500 */
export const kr = (n: number) => `kr ${Math.round(n).toLocaleString('nb-NO')}`;
const nameOf = (b: Pick<ProjectBudget, 'number' | 'name'>) => (b.number ? `${b.number} ${b.name}` : b.name);

/** The forecast in one line: «ligger an til 14 t over» or «12 t under», or null without a progress. */
export function forecastLine(b: ProjectBudget): string | null {
  if (b.forecast == null || b.overrun == null) return null;
  if (Math.abs(b.overrun) < 0.05)
    return T('Ligger an til å gå i null: {f} t ved ferdig.', { f: h1(b.forecast) });
  return b.overrun > 0
    ? T('Ligger an til {over} t over budsjett: {f} t ved ferdig.', { over: h1(b.overrun), f: h1(b.forecast) })
    : T('Ligger an til {under} t under budsjett: {f} t ved ferdig.', {
        under: h1(-b.overrun),
        f: h1(b.forecast),
      });
}

const NUMBERS = ['null', 'ett', 'to', 'tre', 'fire', 'fem', 'seks', 'sju', 'åtte', 'ni', 'ti'];
/** Days from `today` to the last of its month, 0 on the last day. */
export function daysLeftInMonth(today: string): number {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate() - d;
}
/** Within this many days of the month's end, the month close is said. */
export const CLOSE_WINDOW = 5;

/**
 * What Hugin points out about the projects without being asked, in this order: the jobs heading over
 * their budget, furthest first; jobs past the warning with no progress said; the month about to close
 * with projects whose hours are not approved. Each has one button, to the project or to the hours.
 */
export function budgetNudges({
  budgets,
  month,
  today,
  word,
}: {
  budgets: ProjectBudget[];
  /** this month's project groups from the report, approved and waiting per project */
  month: ProjectGroup[] | undefined;
  today: string;
  word: { lower: string; many: string };
}): Nudge[] {
  const out: Nudge[] = [];
  const open = (b: ProjectBudget) => ({
    key: 'open',
    label: T('Åpne {what}', { what: word.lower }),
    lines: [],
    safe: true,
    apply: { kind: 'open' as const, to: `/prosjekter?prosjekt=${b.project_id}` },
  });
  const live = budgets.filter((b) => b.active && b.budget > 0);
  for (const b of live
    .filter((x) => x.progress != null && x.progress < 100 && (x.overrun ?? 0) >= 1)
    .sort((a, c) => (c.overrun ?? 0) - (a.overrun ?? 0)))
    out.push({
      key: `budget-over-${b.project_id}`,
      text: T('{name}: {used} % av timene brukt, du sa {done} % ferdig, ligger an til {over} t over', {
        name: nameOf(b),
        used: b.percent ?? 0,
        done: b.progress ?? 0,
        over: h1(b.overrun ?? 0),
      }),
      choice: open(b),
    });
  for (const b of live.filter((x) => x.progress == null && x.warn))
    out.push({
      key: `budget-warn-${b.project_id}`,
      text: T('{name}: {used} % av timene brukt. Hvor langt er jobben kommet?', {
        name: nameOf(b),
        used: b.percent ?? 0,
      }),
      choice: open(b),
    });
  const left = daysLeftInMonth(today);
  const waiting = (month ?? []).filter((g) => g.project_id != null && (g.waiting ?? 0) > 0).length;
  if (left <= CLOSE_WINDOW && waiting > 0) {
    const when =
      left === 0
        ? T('Måneden lukkes i dag')
        : left === 1
          ? T('Måneden lukkes i morgen')
          : T('Måneden lukkes om {n} dager', { n: left });
    const what =
      waiting === 1
        ? T('ett {what} har timer som ikke er godkjent', { what: word.lower })
        : T('{n} {what} har timer som ikke er godkjent', {
            // number words are Norwegian; other languages get the digit
            n: useI18n.getState().locale === 'nb' ? (NUMBERS[waiting] ?? waiting) : waiting,
            what: word.many.toLowerCase(),
          });
    out.push({
      key: `month-close-${today.slice(0, 7)}`,
      text: `${when}, ${what}`,
      choice: {
        key: 'hours',
        label: T('Åpne timene'),
        lines: [],
        safe: true,
        apply: { kind: 'open', to: `/arbeidstid?periode=maned&mnd=${today.slice(0, 7)}` },
      },
    });
  }
  return out;
}
